import { FC, memo } from 'react'
import { Card, CardProps, Flex, Divider, Stack, HStack } from '@chakra-ui/react'

import {
  ProfessionalContractType,
  ProfessionalContractTypeFilterKeyString,
  ProfessionalContractTypeKeyString,
} from 'InstitutionalManagement/types'
import { CustomIconViewer, Id, NoveltyBadge, Status, TextWithCharacterLimit } from 'components'
import { OwnerIcon } from 'components/visualHelps'

type Props = CardProps & {
  professionalContractTypeFilter?: ProfessionalContractTypeFilterKeyString
  professionalContractType: ProfessionalContractType
}

const ProfessionalContractTypeCard: FC<Props> = ({
  professionalContractType,
  professionalContractTypeFilter,
  children,
  ...cardProps
}) => {
  function setProfessionalContractTypeValueByKey(key: string): string {
    const professionalContractTypeWithKeyString: ProfessionalContractTypeKeyString = { ...professionalContractType }
    return (professionalContractTypeWithKeyString[`professional_contract_type_${key}`] ||
      professionalContractTypeWithKeyString[key]) as string
  }

  function setProfessionalContractTypeFilterValueByKey(key: string): string {
    if (!professionalContractTypeFilter) return ''
    return professionalContractTypeFilter[`professional_contract_type_${key}`] || professionalContractTypeFilter[key]
  }

  return (
    <Card p={2} width="100%" height="100%" className="accessible" _hover={{ bg: 'lightPrimary' }} {...cardProps}>
      <Stack>
        <Flex align="center" gap={1} justifyContent="space-between">
          <Flex align="center" gap={2}>
            <Id id={setProfessionalContractTypeValueByKey('id')} />
            <NoveltyBadge date={setProfessionalContractTypeValueByKey('created_at')} />
          </Flex>
          <Flex align="center" gap={2}>
            <OwnerIcon entity={professionalContractType} />

            <Status status={setProfessionalContractTypeValueByKey('status') as string} />
          </Flex>
        </Flex>
        <Divider color="borderColor" />

        <Flex flexWrap="wrap" justify="space-between" gap={2}>
          <HStack>
            <CustomIconViewer
              variant="twoTone"
              boxSize={8}
              color={setProfessionalContractTypeValueByKey('color')}
              icon={setProfessionalContractTypeValueByKey('icon')}
            />

            <Stack spacing={0}>
              <TextWithCharacterLimit hasHighlight highlightQuery={setProfessionalContractTypeFilterValueByKey('name')}>
                {setProfessionalContractTypeValueByKey('name')}
              </TextWithCharacterLimit>
              <TextWithCharacterLimit
                hasHighlight
                highlightQuery={setProfessionalContractTypeFilterValueByKey('description')}
                variant="secondary"
              >
                {setProfessionalContractTypeValueByKey('description')}
              </TextWithCharacterLimit>
            </Stack>
          </HStack>
        </Flex>

        {children && <Divider color="borderColor" />}

        {children}
      </Stack>
    </Card>
  )
}

export default memo(ProfessionalContractTypeCard)
